import {  useEffect } from "react";
import { useForm, useFieldArray, useWatch  } from "react-hook-form";
import { useQuery} from "@tanstack/react-query";
import { get, post, put } from "@utils/axios_api";
import { toHHmmss, toDateObj } from "@utils/customFunction";

const fetchCategoryList = async () => {
  const response = await get("/common/categoryList");
  return response;
};
const fetchEduTypeList = async (groupCode) => {
  const response = await post("/common/codeList", { groupCode });
  return response;
};
const fetchInstructorList = async () => {
  const response = await get("/admin/lecture/instructorList");
  return response;
};

const formatYMD = (d) => new Date(d).toISOString().slice(0, 10);
const flattenSchedule = (days) =>
  days.flatMap((d) =>
    d.classes.map((c, idx) => ({
      eduDt: d.date,
      classNo: idx + 1,
      subject: c.subject,
      startTm: toHHmmss(c.startTm),
      endTm: toHHmmss(c.endTm),
      instructorId: c.instructorId,
    }))
  );

const defaultFormValues = {
  lectureNm: "",
  eduCategoryId: "",
  eduType: "",
  firstDt: "",
  lastDt: "",
  applyStartDt: "",
  applyEndDt: "",
  eduLaw: "",
  eduGoal: "",
  companyNm: "",
  managerNm: "",
  managerEmail: "",
  managerPhoneNo: "",
  eduHours: 0,
  eduPee: 0,
  eduStdntLimit: 0,
  zipcode: "",
  address1: "",
  address2: "",
  eduContents: "",
  days: [],
};
export function useLectureForm({isEdit, selectedLectureId  }) {

  const methods = useForm({
    defaultValues: defaultFormValues, // defaultValues : react-hook-form 초기값 설정 옵션
    shouldFocusError: false, // Focus 방지
  });

  const { control, setValue, getValues } = methods;


  const {
    fields: dayFields,
    append: appendDay,
    remove: removeDay,
  } = useFieldArray({ control, name: "days" });

  /* 현재 날짜값 실시간 구독 */
  const firstDt = useWatch({ control, name: "firstDt" });
  const lastDt = useWatch({ control, name: "lastDt" });

  useEffect(() => {
    if (!firstDt) {
      if (dayFields.length) setValue("days", []); // 날짜 지워지면 초기화
      return;
    }

    const list = lastDt && lastDt !== firstDt ? [formatYMD(firstDt), formatYMD(lastDt)] : [formatYMD(firstDt)];
    const cur  = getValues("days");

    if (cur.length === list.length && cur.every((d, i) => d.date === list[i])) return;

    const next = list.map((d, i) => ({
      date: d,
      classes: cur[i]?.classes?.length ? cur[i].classes : [{ subject: "", startTm: "", endTm: "", instructorId: "" }],
    }));
    setValue("days", next, { shouldDirty: true });
  }, [firstDt, lastDt]);


  useEffect(() => {
    if (!isEdit || !selectedLectureId) return;
  
    (async () => {
      const response = await get("/admin/lecture/lectureDetail", { params: { lectureId: selectedLectureId } });
      const byDate = {};
      response.schedule.forEach((s) => {
        if (!byDate[s.eduDt]) byDate[s.eduDt] = [];
        byDate[s.eduDt].push({
          classNo:      s.classNo,
          subject:      s.subject,
          startTm:      toDateObj(s.startTm),
          endTm:        toDateObj(s.endTm),
          instructorId: s.instructorId,
        });
      });
  
      const days = Object.keys(byDate)
        .sort()                     // 날짜 순
        .map((d) => ({
          date: d,
          classes: byDate[d]
            .sort((a, b) => a.classNo - b.classNo)
            .map(({ classNo, ...rest }) => rest), // classNo 는 index로 다시 생성하므로 제외
        }));
  
      methods.reset({ ...response.lecture, days });
    })();
  }, [isEdit, selectedLectureId, methods]);


  const { data:categoryList } = useQuery({
    queryKey: ["selectAdminCategoryListForm"],
    queryFn: () => fetchCategoryList(),
    // staleTime: Infinity,
  });

  const { data:eduTypeList } = useQuery({
    queryKey: ["selectAdminEduTypeListForm", "EDU_TYPE" ],
    queryFn: () => fetchEduTypeList("EDU_TYPE"),
    // staleTime: Infinity,
  });

  const { data:instructorList } = useQuery({
    queryKey: ["selectAdminInstructorListForm" ],
    queryFn: () => fetchInstructorList(),
    // staleTime: Infinity,
  });

  const buildPayload = (data) => {
    const { days, ...lecture } = data;
    const schedule = flattenSchedule(days);
    if (isEdit) lecture.lectureId = selectedLectureId;
    return { lecture, schedule };
  };
  
  const createLecture = (data) => post("/admin/lecture/insertLecture", buildPayload(data));
  const updateLecture = (data) => put("/admin/lecture/updateLecture", buildPayload(data));


  return {
    methods, categoryList, eduTypeList, instructorList, 
    createLecture, updateLecture,
    dayFields, appendDay, removeDay,
  };
}
